/**
 * The world's scalar field. `density(x, y) > 0` means solid rock.
 *
 * This one function is the single source of truth for BOTH the rendered terrain
 * mesh (marching squares over it) and gameplay collision (analytic sampling +
 * gradient push-out), so what you see is exactly what you hit.
 *
 * Structure follows PRD §10: an authored seabed profile and authored landmarks,
 * with procedural detail layered on top so it never reads as "randomly generated".
 */

import { hyp } from '../core/math';
import { fbm1, fbm2, ridge2, Rng } from '../core/noise';
import { WORLD, WORLD_SEED } from './config';
import { AUTHORED, profileY } from './level';

export { profileY };

export interface Feature {
  /** blob adds rock, tunnel and chamber carve it away. */
  kind: 'tunnel' | 'blob' | 'chamber';
  /** Bounding box, already inflated by the feature's radius. */
  x0: number; x1: number; y0: number; y1: number;
  /** Polyline points for tunnels: [x, y, r, x, y, r, ...] */
  pts?: number[];
  cx?: number; cy?: number; rx?: number; ry?: number;
}

// ---------------------------------------------------------------------------
// Seabed profile
// ---------------------------------------------------------------------------

/**
 * Authored profile + procedural relief. Cached per column since it is 1-D, in
 * a direct-mapped table rather than a `Map`: this is the single hottest lookup
 * in the game — every `density` sample starts here, and the beam march alone
 * makes ~1700 of them a frame — and a `Map<number, number>` get/set costs
 * 63.5 ns against 9.8 ns for a keyed typed-array slot, measured over 8M
 * lookups. 2^17 slots covers 16 km of world at eighth-metre columns; beyond
 * that a key simply collides and is recomputed, which is correct, just slower.
 */
const COL_BITS = 17;
const COL_MASK = (1 << COL_BITS) - 1;
/** Sentinel is a column ~33 000 km out, i.e. one this world cannot ask for. */
const profKey = new Int32Array(COL_MASK + 1).fill(0x7fffffff);
const profVal = new Float64Array(COL_MASK + 1);

export function seabedY(x: number): number {
  const key = Math.round(x * 8);
  const slot = key & COL_MASK;
  if (profKey[slot] === key) return profVal[slot];
  // Evaluated at the COLUMN CENTRE, not at the caller's x. The old cache keyed
  // on the column but computed from whatever raw x arrived first, so the value
  // for a column depended on who asked for it first — sampling the same world
  // in a different order returned a different seabed, by up to the profile's
  // slope across 6.25 cm. Harmless on screen and invisible in a diff, but it
  // made the field an impure function of position, which is the sort of thing
  // that makes a golden trace drift for no reason anyone can find later.
  const qx = key / 8;
  const base = profileY(qx);
  // Three scales of relief: broad swells, dunes, and fine chop.
  const swell = fbm1(qx * 0.0032, 3, WORLD_SEED) * 22;
  const dune = fbm1(qx * 0.017, 3, WORLD_SEED + 71) * 8.5;
  const chop = fbm1(qx * 0.09, 2, WORLD_SEED + 913) * 2.2;
  const v = base + swell + dune + chop;
  profKey[slot] = key;
  profVal[slot] = v;
  return v;
}

// ---------------------------------------------------------------------------
// Features: authored landmarks + deterministic procedural outcrops
// ---------------------------------------------------------------------------

const SECTOR = 256;
/**
 * Metres over which an authored feature's contribution fades in from its own
 * bounding box, IN THE FIELD ITSELF.
 *
 * The box is the ellipse exactly — `blobFeature` sets `x0 = cx - rx` — and the
 * ellipse's value is zero there, so the shape needs no feathering. The noise
 * does: `fbm2(...) * 2.8` is still live at the box and carves a fringe that
 * stops dead at a straight line. Tunnels are worse, because `distToPolyline`
 * returns the tube radius at the box rather than zero, so the field steps by
 * the entire rock depth — measured at 135 against a shade saturation of 20.
 *
 * That step is a real vertical WALL in the mesh, not a shading artefact, which
 * is why softening the shade hint could never remove it and three attempts to
 * do so all failed. It has to come out of the field.
 *
 * Kept small, and capped per feature below, because the truncation is
 * load-bearing: smoothing it wholesale walls off the octopus arena and seals a
 * vault, which is what `npm run audit` exists to catch.
 */
const FEATURE_FEATHER = 2;

const sectorCache = new Map<number, Feature[]>();

function tunnelFeature(pts: number[]): Feature {
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (let i = 0; i < pts.length; i += 3) {
    const r = pts[i + 2];
    x0 = Math.min(x0, pts[i] - r); x1 = Math.max(x1, pts[i] + r);
    y0 = Math.min(y0, pts[i + 1] - r); y1 = Math.max(y1, pts[i + 1] + r);
  }
  return { kind: 'tunnel', pts, x0, x1, y0, y1 };
}

function blobFeature(
  cx: number, cy: number, rx: number, ry: number,
  kind: 'blob' | 'chamber' = 'blob',
): Feature {
  return { kind, cx, cy, rx, ry, x0: cx - rx, x1: cx + rx, y0: cy - ry, y1: cy + ry };
}

export const AUTHORED_FEATURES: Feature[] = AUTHORED.map((f) =>
  f.kind === 'tunnel' ? tunnelFeature(f.pts!) : blobFeature(f.cx!, f.cy!, f.rx!, f.ry!, f.kind),
);

/** Deterministic procedural outcrops, boulders and arches for one 256 m sector. */
/**
 * Authored features, indexed by the same sector grid the procedural ones use.
 * A feature wider than a sector is listed in every sector it spans, so a lookup
 * never has to widen its range to find one.
 */
let authoredIndex: Map<number, Feature[]> | null = null;

function authoredBySector(): Map<number, Feature[]> {
  if (authoredIndex) return authoredIndex;
  const m = new Map<number, Feature[]>();
  for (const f of AUTHORED_FEATURES) {
    const s0 = Math.floor(f.x0 / SECTOR);
    const s1 = Math.floor(f.x1 / SECTOR);
    for (let s = s0; s <= s1; s++) {
      let b = m.get(s);
      if (!b) m.set(s, (b = []));
      b.push(f);
    }
  }
  authoredIndex = m;
  return m;
}

function sectorFeatures(sector: number): Feature[] {
  const cached = sectorCache.get(sector);
  if (cached) return cached;
  const rng = new Rng(WORLD_SEED ^ (sector * 2654435761));
  const out: Feature[] = [];
  const x0 = sector * SECTOR;

  const count = rng.int(3, 6);
  for (let i = 0; i < count; i++) {
    const x = x0 + rng.range(0, SECTOR);
    const sy = seabedY(x);
    const kind = rng.next();
    if (kind < 0.55) {
      // Boulder / outcrop resting on the seabed.
      const r = rng.range(3.5, 11);
      out.push(blobFeature(x, sy + r * rng.range(-0.15, 0.45), r * rng.range(0.85, 1.5), r));
    } else if (kind < 0.85) {
      // Rock spire.
      const h = rng.range(9, 26);
      const w = rng.range(2.6, 6);
      out.push(blobFeature(x, sy + h * 0.45, w, h * 0.6));
    } else {
      // Arch: two legs joined by a span, then hollowed by a tunnel.
      const w = rng.range(14, 26);
      const h = rng.range(12, 22);
      out.push(blobFeature(x, sy + h * 0.5, w * 0.62, h * 0.62));
      out.push(tunnelFeature([x - w * 0.28, sy + h * 0.16, h * 0.2, x + w * 0.28, sy + h * 0.16, h * 0.2]));
    }
  }
  if (sectorCache.size > 512) sectorCache.clear();
  sectorCache.set(sector, out);
  return out;
}

// ---------------------------------------------------------------------------
// Craters — the only mutable part of the field
// ---------------------------------------------------------------------------

/**
 * Explosions carve rock. A crater is just another CSG chamber, so it flows
 * through `featuresFor` into the mesher, collision, the lamp's shadow march and
 * every AI avoidance query at once — there is no second representation of a
 * hole to keep in sync.
 *
 * They are bucketed by sector and capped: an unbounded list would make
 * `density` slower every time the player fired.
 */
const craterBuckets = new Map<number, Feature[]>();
const craterOrder: { sector: number; f: Feature }[] = [];
/**
 * Generous on purpose. Craters retire oldest-first, and a vault takes up to
 * five shots to open — with a tight cap, digging elsewhere could re-seal a
 * passage the player had already opened, potentially while they were standing
 * in it. They are cheap: `density` only ever iterates the craters in the
 * sector it is asked about.
 */
const MAX_CRATERS = 400;

/** Bumped whenever the field changes, so the terrain knows to re-mesh. */
export let fieldRevision = 0;

/**
 * Carve a hole of radius `r` at (x, y). Returns the x-span that changed, or
 * null when the blast was in open water and nothing needed doing.
 */
export function carve(x: number, y: number, r: number): { x0: number; x1: number } | null {
  if (r < 1.5) return null;
  // Only spend a crater slot if there is actually rock in the blast.
  const feats = featuresFor(x - r, x + r);
  let solid = false;
  for (let i = 0; i < 9 && !solid; i++) {
    const a = (i / 9) * Math.PI * 2;
    const t = i === 0 ? 0 : r * 0.62;
    if (density(x + Math.cos(a) * t, y + Math.sin(a) * t, feats) > 0) solid = true;
  }
  if (!solid) return null;

  // Craters are slightly wider than tall: a charge going off against a wall
  // should read as a bite out of the face, not a perfect bubble.
  const f = blobFeature(x, y, r * 1.12, r * 0.92, 'chamber');
  const sector = Math.floor(x / SECTOR);
  let bucket = craterBuckets.get(sector);
  if (!bucket) craterBuckets.set(sector, (bucket = []));
  bucket.push(f);
  craterOrder.push({ sector, f });

  // Retire the oldest crater rather than letting the field get slower forever.
  while (craterOrder.length > MAX_CRATERS) {
    const old = craterOrder.shift()!;
    const b = craterBuckets.get(old.sector);
    if (b) {
      const i = b.indexOf(old.f);
      if (i >= 0) b.splice(i, 1);
    }
  }

  fieldRevision++;
  return { x0: f.x0, x1: f.x1 };
}

export function resetCraters() {
  craterBuckets.clear();
  craterOrder.length = 0;
  fieldRevision++;
}

/** Serialised craters, so a save can restore the holes the player blew. */
export function craterState(): number[] {
  const out: number[] = [];
  for (const c of craterOrder) out.push(c.f.cx!, c.f.cy!, c.f.rx!);
  return out;
}

export function restoreCraters(flat: number[]) {
  resetCraters();
  for (let i = 0; i + 2 < flat.length; i += 3) {
    const f = blobFeature(flat[i], flat[i + 1], flat[i + 2], flat[i + 2] * 0.82, 'chamber');
    const sector = Math.floor(flat[i] / SECTOR);
    let bucket = craterBuckets.get(sector);
    if (!bucket) craterBuckets.set(sector, (bucket = []));
    bucket.push(f);
    craterOrder.push({ sector, f });
  }
  fieldRevision++;
}

/** Every feature that can influence the vertical strip [x0, x1]. */
export function featuresFor(x0: number, x1: number): Feature[] {
  const out: Feature[] = [];
  // Authored features are bucketed by sector, like the procedural ones. This
  // used to walk the whole authored list on every call, and `featuresFor` is
  // called two or three times per enemy per frame by the steering code — 4.05 µs
  // a call to return an average of 0.3 features. With Survival's front at 72
  // that alone was over a millisecond of frame time spent scanning past
  // everything in the world that is nowhere near the thing being steered.
  for (let s = Math.floor((x0 - 40) / SECTOR); s <= Math.floor((x1 + 40) / SECTOR); s++) {
    const bucket = authoredBySector().get(s);
    if (bucket) for (const f of bucket) if (f.x1 >= x0 && f.x0 <= x1) out.push(f);
  }
  const s0 = Math.floor((x0 - 40) / SECTOR);
  const s1 = Math.floor((x1 + 40) / SECTOR);
  for (let s = s0; s <= s1; s++) {
    for (const f of sectorFeatures(s)) if (f.x1 >= x0 && f.x0 <= x1) out.push(f);
    const holes = craterBuckets.get(s);
    if (holes) for (const f of holes) if (f.x1 >= x0 && f.x0 <= x1) out.push(f);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Density
// ---------------------------------------------------------------------------

function distToPolyline(pts: number[], x: number, y: number): number {
  // Returns d - r, i.e. signed distance to the swept-radius tube. Negative = inside.
  let best = Infinity;
  for (let i = 0; i + 5 < pts.length; i += 3) {
    const ax = pts[i], ay = pts[i + 1], ar = pts[i + 2];
    const bx = pts[i + 3], by = pts[i + 4], br = pts[i + 5];
    const ex = bx - ax, ey = by - ay;
    const len2 = ex * ex + ey * ey || 1e-6;
    let t = ((x - ax) * ex + (y - ay) * ey) / len2;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const px = ax + ex * t, py = ay + ey * t;
    const r = ar + (br - ar) * t;
    const d = hyp(x - px, y - py) - r;
    if (d < best) best = d;
  }
  if (pts.length === 3) best = hyp(x - pts[0], y - pts[1]) - pts[2];
  return best;
}

function blobValue(f: Feature, x: number, y: number): number {
  const dx = (x - f.cx!) / f.rx!;
  const dy = (y - f.cy!) / f.ry!;
  const n = Math.sqrt(dx * dx + dy * dy);
  return (1 - n) * Math.min(f.rx!, f.ry!);
}

/**
 * Solid where > 0. `feats` may be pre-filtered for the column being sampled —
 * the mesher passes a per-chunk list so this stays cheap in the inner loop.
 */
export function density(
  x: number, y: number, feats?: Feature[],
  /**
   * Metres over which an authored feature's contribution fades in from its
   * bounding box. 0 — the default, and what collision, the contour and every
   * golden trace use — is the hard clip the levels were tuned against.
   *
   * The clip is load-bearing for the WORLD: chambers carve past their ellipse
   * because of it, and smoothing the field walls off the octopus arena and
   * seals a vault. It is also a cliff — 59 of 84 boxes jump by more than 3, the
   * worst by 135 — and anything reading the field as "how deep in the rock am
   * I" draws that cliff as a straight-edged rectangle around every cave.
   *
   * Only the shade hint asks for the softened field. It does not have to agree
   * with collision to the metre; it only has to look like light.
   */
  soften = FEATURE_FEATHER,
): number {
  const sb = seabedY(x);
  let d = sb - y;

  // Ridge detail only matters within a band of the boundary; skip deep interior.
  if (d > -46 && d < 46) {
    const strata = ridge2(x * 0.021, y * 0.055, 3, WORLD_SEED + 17);
    const rough = fbm2(x * 0.055, y * 0.062, 3, WORLD_SEED + 401);
    // A finer octave near the surface gives the silhouette its broken, rocky edge.
    const chip = fbm2(x * 0.34, y * 0.31, 2, WORLD_SEED + 733);
    d += strata * 6.4 - 3.0 + rough * 4.2 + chip * 1.35;
  }

  // Additive and subtractive features are accumulated separately, and the
  // subtraction is applied last. Evaluating them in list order let a
  // procedural boulder that happened to sit over an authored tunnel plug it
  // back up — which is invisible until a reward is sealed inside a hill.
  // Carved space always wins.
  const list = feats ?? featuresFor(x - 1, x + 1);
  let add = -Infinity;
  let cut = Infinity;
  for (let i = 0; i < list.length; i++) {
    const f = list[i];
    if (x < f.x0 || x > f.x1 || y < f.y0 || y > f.y1) continue;
    let t = 1;
    if (soften > 0) {
      // Capped at a quarter of the shorter side, so a feather can never eat the
      // feature it is feathering.
      const w = Math.min(soften, Math.min(f.x1 - f.x0, f.y1 - f.y0) * 0.25);
      const edge = Math.min(x - f.x0, f.x1 - x, y - f.y0, f.y1 - y);
      t = edge >= w ? 1 : Math.max(0, edge / w);
      t = t * t * (3 - 2 * t);
      if (t <= 0) continue;
    }
    if (f.kind === 'blob') {
      let v = blobValue(f, x, y);
      if (v > -6) v += fbm2(x * 0.13, y * 0.13, 2, WORLD_SEED + 55) * 1.9;
      if (t < 1) v = d + (v - d) * t;
      if (v > add) add = v;
    } else if (f.kind === 'chamber') {
      // CSG subtract: positive inside the ellipse means "remove this rock".
      let v = blobValue(f, x, y);
      if (v > -8) v += fbm2(x * 0.09, y * 0.11, 3, WORLD_SEED + 311) * 2.8;
      let c = -v;
      if (t < 1) c = d + (c - d) * t;
      if (c < cut) cut = c;
    } else {
      let v = distToPolyline(f.pts!, x, y);
      if (v < 8) v += fbm2(x * 0.075, y * 0.075, 3, WORLD_SEED + 202) * 2.4;
      if (t < 1) v = d + (v - d) * t;
      if (v < cut) cut = v;
    }
  }
  if (add > d) d = add;
  if (cut < d) d = cut;

  // Never leave rock poking out of the water surface.
  if (y > WORLD.surfaceY - 1.5) d = Math.min(d, WORLD.surfaceY - 1.5 - y);
  return d;
}

/** Outward-pointing surface normal (unit-ish) at a point, via central differences. */
export function gradient(
  x: number, y: number, out: { x: number; y: number }, h = 0.55,
  // Callers that already hold a feature list for this neighbourhood should pass
  // it. `avoid` fetches one and then called this, which fetched another for the
  // same half-metre — 4 µs of the 9.16 µs a gradient cost.
  given?: Feature[],
) {
  const feats = given ?? featuresFor(x - h - 1, x + h + 1);
  const dx = density(x + h, y, feats) - density(x - h, y, feats);
  const dy = density(x, y + h, feats) - density(x, y - h, feats);
  const len = hyp(dx, dy) || 1e-5;
  // Density increases into the rock, so the outward normal is -grad.
  out.x = -dx / len;
  out.y = -dy / len;
  return len / (2 * h);
}

/**
 * Vertical band that actually needs meshing for the strip [x0, x1].
 * Everything below is solid, everything above is open water.
 */
export function detailBand(x0: number, x1: number): { lo: number; hi: number } {
  let sMin = Infinity, sMax = -Infinity;
  const steps = 10;
  for (let i = 0; i <= steps; i++) {
    const s = seabedY(x0 + ((x1 - x0) * i) / steps);
    if (s < sMin) sMin = s;
    if (s > sMax) sMax = s;
  }
  let lo = sMin - 52;
  let hi = sMax + 52;
  // Queried WIDE, and with room. The band is where geometry exists at all: past
  // it there is no mesh, so its edge is not a fade, it is rock ending in mid-air
  // on a straight line. Asking over a padded x range means every chunk near a
  // feature expands with it rather than only the ones it overlaps, so there is
  // no step at a chunk seam; the bigger vertical margin puts the end of the band
  // somewhere the lamp was never going to reach.
  const PAD = 120;
  for (const f of featuresFor(x0 - PAD, x1 + PAD)) {
    if (f.y0 - 30 < lo) lo = f.y0 - 30;
    if (f.y1 + 30 > hi) hi = f.y1 + 30;
  }
  return {
    lo: Math.max(WORLD.yMin, lo),
    hi: Math.min(WORLD.yMax, hi),
  };
}

/**
 * First open-water y above the seabed at x — used to place props on the floor.
 *
 * The search starts just above the local seabed rather than at the water
 * surface: with the world running to −2500 m, marching down from y = 6 would
 * cost hundreds of `density` samples per prop in the deep zones.
 */
export function floorAt(x: number, from = seabedY(x) + 90): number {
  const feats = featuresFor(x - 2, x + 2);
  let y = Math.min(WORLD.yMax, from);
  let step = 4;
  while (y > WORLD.yMin && step > 0.05) {
    if (density(x, y, feats) > 0) {
      y += step;
      step *= 0.4;
    }
    y -= step;
  }
  return y;
}

/** True if a sphere of `r` at (x, y) is clear of rock. */
export function isClear(x: number, y: number, r: number): boolean {
  const feats = featuresFor(x - r - 1, x + r + 1);
  if (density(x, y, feats) > -r * 0.4) return false;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    if (density(x + Math.cos(a) * r, y + Math.sin(a) * r, feats) > 0) return false;
  }
  return true;
}
